import type { OperatorMe } from "@/lib/api";
import { isRecruiting, verticalFrom } from "@/lib/verticalCopy";

/**
 * Where an operator lands after login, or on `/`.
 *
 * The two dashboards render the same queue under different framing, so the
 * only question is which framing this operator's org gets — and that is the
 * org's **vertical**, which `GET /api/v1/operator/me` now returns outright.
 *
 * ## This used to ask `workflow_keys.length > 0`
 *
 * That was proxy 2 of the five the vertical-aware case model retires. It
 * meant *"this org has a custom workflow"*, which is not the same statement
 * as *"this org is a recruiter"* — the first leasing org to get a bespoke
 * flow would have landed on the recruiting dashboard. B.3 / A.2 ("Rockwell as
 * configuration #1") is precisely the change that puts a leasing org on the
 * definition path, so the proxy was one roadmap row away from being wrong,
 * not hypothetically wrong.
 *
 * `workflow_keys` is still a real and useful fact about configuration — it is
 * what the Phase-0 flow picker will enumerate — it just never answered this
 * question.
 *
 * ## Still keyed off configuration, never off org name or subdomain
 *
 * The rule `relationshipOptions.ts` set holds, and the vertical satisfies it
 * better than the proxy did: no org is named in front-end code, and any org
 * seeded `hr_recruiting` reads correctly without a code change.
 *
 * ## Absent → the leasing dashboard, which is a no-op
 *
 * `industry` is optional in the contract because a consumer pinned against an
 * older server keeps parsing. Absent means *unknown*, and `verticalFrom()`
 * reads it as `"unknown"`, which is not recruiting and so routes to the
 * leasing dashboard: the incumbent, and where every org went before this
 * field existed. (Unknown shows no rating there; see `showsApplicantRating`.) Callers that can afford to wait for a real
 * answer should gate on `useOperatorVertical().isResolved` rather than
 * treating the fallback as fact.
 */

/** Landing paths. Exported so tests and callers cannot drift on a string. */
export const LEASING_HOME = "/dashboard";
export const RECRUITING_HOME = "/recruiting/dashboard";

/**
 * Does this org run the recruiting product?
 *
 * Deliberately tolerant: the field is absent on older servers, and an org with
 * no membership has no configuration at all. Both mean "not recruiting", not
 * "unknown" — there is no third landing page to send someone to.
 */
export function isRecruitingOrg(me: OperatorMe | null | undefined): boolean {
  return isRecruiting(verticalFrom(me?.org?.industry));
}

export function operatorHome(me: OperatorMe | null | undefined): string {
  return isRecruitingOrg(me) ? RECRUITING_HOME : LEASING_HOME;
}
